/**
 * ── THE BRICK CONSTANTS (`axon.probeVolume/5`, spec §1.1) ───────────────────────────────────────
 *
 * The numbers the compiler, the scatter, the loader, the file format and the folded shader graph
 * must all agree on. They live alone in a dependency-free module for the same reason
 * `probeFile.ts` has no `three` import: five callers in three environments (browser lazy GI chunk,
 * node baker under `--experimental-strip-types`, vitest) share ONE definition, because a constant
 * that is re-typed at its second use is two constants that agree until someone edits one.
 *
 * PURE — no `three`, no DOM, no clock. Listed in `PURE_FILES` (tests/purity.test.ts).
 *
 * ── WHY THESE VALUES AND NOT THE OBVIOUS ONES ───────────────────────────────────────────────────
 *
 * `BRICK_N = 4`, not 8, and that CONTRADICTS the performance audit's T11 deliberately. T11
 * optimises the boundary-layer multiplier (N=4 → 1.95x at the dense asymptote, N=8 → 1.42x). The
 * owner's ruling outranks it: *"MEMORY IS NOT THE PROBLEM. Do not optimise bytes at the cost of
 * quality or simplicity."* What N=8 costs is SPARSITY GRANULARITY — a brick is the unit that
 * exists-or-doesn't, and at N=8 on a 1.2 m base the finest brick is a 9.6 m cube, larger than most
 * Mars interior rooms, so "fine density around this corridor" stops being expressible at all. At
 * N=4 it is a 4.8 m cube. This is one constant read by the compiler, the scatter and the shader,
 * so N=8 stays a one-line experiment plus a `bakerVersion` bump. Recorded, not forgotten.
 *
 * The eight bands live INSIDE the brick (footprint z = SUB_VOLUMES · 5) rather than in separate
 * textures (T12) or Z-stacked across the whole atlas (v1). Eight separate textures would cost 8
 * bindings + 1 indirection = 9, on top of a standard material's 5 maps + shadow + PMREM, against
 * WebGPU's default `maxSampledTexturesPerShaderStage` of 16 and WebGL2's `MAX_TEXTURE_IMAGE_UNITS`
 * floor of 16 — landing on the limit on the rung we cannot measure. Inside the brick, the 7 SH
 * fetches for one fragment land in one contiguous block, and the sun-visibility band sits one
 * stride past them in the same 3D texture.
 *
 * ── AND IT NEEDS NO PADDING, WHICH IS WHY `ATLAS_PADDING` IS DELETED RATHER THAN REPLACED ───────
 *
 * The sample coordinate inside a brick is `local ∈ [0, N)`; plus the half-texel centre offset the
 * texel coordinate is in `[0.5, N + 0.5)`. Hardware trilinear at that range reads planes `0..N`
 * and never plane `N+1`. So a fetch in sub-volume `t` cannot bleed into `t ± 1` and a fetch in
 * brick `b` cannot bleed into `b ± 1`: the shared-boundary layer IS the border, and it is
 * load-bearing rather than redundant.
 *
 * ── ⛔ NAMED LOSS 1 of 3 (O3) · THE OCTAHEDRAL DEPTH BANDS AND THE CHEBYSHEV TERM ARE DELETED ───
 *
 * `git show c709d13 -- src/render/gi/probeOct.ts src/render/gi/sparseFetch.ts` is the whole
 * implementation. What left the build is a CAPABILITY — Majercik et al., JCGT 8(2) 2019 §5 leak
 * rejection — and it is named here, at the file that defines what a probe IS, because that is what
 * it was a term IN. It was not a shader feature with a switch; it was 64 of a probe's 71 bands.
 *
 * ⭐ WHY, IN FOUR MEASURED NUMBERS. It was **90 % of the bytes** (512 B of 568 per probe) and
 * **82 % of the frame cost** (the indirect term measured 7.54 ms/Mpix with the cage and
 * **1.14 ms/Mpix** with it folded out, against a 7.20 ms/Mpix budget — i.e. 105 % of the whole
 * frame budget against 16 %), for a term that shipped **OFF** (`DEFAULT_VISIBILITY_MIX = 0`), and
 * which when switched ON measured **arcade −1.0 % / atrium −3.9 %** on the correct authoring: the
 * CONTROL moved four times harder than the test. A term that dims an open courtyard more than a
 * covered aisle is not a leak fence in the authoring where it matters.
 *
 * ⭐ AND THE FINDING A RE-IMPLEMENTER NEEDS MOST, WHICH IS NEW AND IS NOT ABOUT THE LAYOUT. The
 * owner's real 46 MB Sponza atlas was decoded off disk (5,200,000 stored depth texels, `farM 455`):
 * median stored mean **191.25 m**, p95 **454.75 m**, and **30.7 % of texels at or above 410 m** —
 * in a scene whose atrium is ~30 m across. The mechanism: the far plane is set by the SKY DOME
 * (`bakeFarForSky(400, halfDiag)`), the facing pass hides the sky and clears to `rgb(0,1,0)`, so
 * "no geometry" is encoded as EXACTLY `farM`, and the cosine lobe then averaged that sentinel
 * together with real wall distances. `t = max(r − mean, 0)` is therefore ~0 for every cage radius
 * any authoring can produce, and the expected Chebyshev factor is 0.961 at a level-0 cage and
 * 0.877 at a level-2 one — a uniform dimmer whose depth tracks the CAGE RADIUS rather than the
 * occlusion. That predicts every measurement above. **A layout fix does not address it.**
 *
 * ── THE TWO EXITS, UNCHANGED, IF THE CAPABILITY IS EVER REBUILT ────────────────────────────────
 *
 *   1. **COST — a different layout, not a smaller map.** A control build that kept all eight
 *      `textureLoad`s but pinned every one to a FIXED band measured 2.63 ms/Mpix, so of the
 *      ~6.4 ms/Mpix the cage added, ~1.5 was the eight fetches and **~4.9 was the SCATTER**: a
 *      probe's depth bands were `BRICK_EDGE` atlas PLANES apart and a plane of Sponza's atlas is
 *      ~24 KB, so eight cage probes looking in eight directions touched addresses ~120 KB apart,
 *      and neighbouring fragments picked different bands, so the divergence was per-quad. The fix
 *      is DDGI's 2D probe atlas: its own texture with the band on the FASTEST axis,
 *      `x = probe·OCT_TEXELS + j`. That is a second payload SECTION and a `BAKER_VERSION` bump.
 *   2. **QUALITY — the form, not the map.** Ours was SEPARABLE (one cage-wide visible fraction)
 *      because hardware trilinear had already averaged the cage; the paper's is per-probe. The
 *      named cheaper exit is to duplicate the L0 band and correct by the DC RATIO,
 *      `(Σ wVv·c0 / Σ wVv) ÷ (Σ wv·c0 / Σ wv)`, exact for the direction-free term the leak lives
 *      in, at 8 more loads.
 *
 * ⚠️ **BOTH EXITS REPLACE THESE BANDS RATHER THAN ADD TO THEM**, which is why "make it optional"
 * was rejected as well: an option preserves the one configuration both improvements delete, at the
 * cost of a permanent second program variant — and `LightsNode.customCacheKey()` hashes
 * `light.id`/`castShadow`/`map`/`colorNode` and NOT a band count, so two arms would share one
 * pipeline-cache entry and whichever compiled first would win for the life of the page. That is
 * R1.6-D. The re-entry GATE is written down as a number rather than as a feeling: `rejection ≥
 * 0.60` of a MEASURED leak with `control drift ≤ 0.03`, and the term under 2.0 ms/Mpix. What the
 * remaining leak defence is, and what it is worth, is at `probeDilate.ts`'s named loss 3.
 */

/** Voxel cells along one brick edge. A brick STORES (N+1)³ probes for N³ cells. */
export const BRICK_N = 4;

/** Stored probes per brick edge — the shared-boundary layer (Unreal's `PaddedBrickSize`). */
export const BRICK_EDGE = BRICK_N + 1; // 5

/** Stored probes per brick. The length of `CompiledBrick.captures`. */
export const BRICK_PROBES = BRICK_EDGE * BRICK_EDGE * BRICK_EDGE; // 125

/**
 * ⭐ THE SH HALF — nine L2 SH coefficients plus the validity scalar, in SEVEN RGBA texels.
 *
 * 9 × 3 + 1 = 28 floats = exactly 7 × RGBA, with no spare channel. That exactness is why the
 * sun-visibility scalar could not be squeezed in beside them: the atlas is `LinearFilter` on all
 * three axes BY REQUIREMENT (the validity divide depends on the hardware doing the trilinear
 * blend), and hardware filtering operates on the STORED NUMBER — so any two-values-in-one-float
 * encoding is destroyed by the first interpolation between two probes and yields a plausible
 * number that is neither. `probeBrick`'s own rule, restated: *a sentinel and a measurement must
 * not share a channel*, and now also *two measurements must not share a FILTERED channel*.
 *
 * L2 (nine coefficients) rather than L1 (four) is a SEPARATE decision, taken separately and kept:
 * the realised cost is 1.75× and not 2.25× (L1 is 13 floats ⇒ 4 RGBA bands, because the validity
 * premultiply needs a free `.w`, so the packing is texel-quantised), and after the depth cut the
 * WHOLE indirect term is 1.14 of a 7.20 ms/Mpix budget — there is no pressure left to spend
 * picture on. We also have no lightmaps, so this field is the only carrier of indirect light for
 * STATIC geometry too, which is why UE's L1 Volumetric Lightmaps are not a precedent. Reopen only
 * on a tier-1 tablet measurement, which nobody has taken.
 */
export const SH_TEXELS = 7;

/**
 * ⭐ THE SUN-VISIBILITY BAND'S INDEX (`axon.probeVolume/5`) — UE's `FVolumeLightingSampleData.
 * DirectionalLightShadowing`, one band past the SH.
 *
 * Packed `vec4(validity · sunVis, 0, 0, validity)` — see `probeGrid.packShTexel`. The duplicated
 * validity in `.w` is the design and it costs nothing (the band exists either way): it makes the
 * sun's shadow term SELF-CONTAINED at one sample and one divide, with no dependence on the SH
 * cage's `s6.w`, so the lit path's 1-load/7-sample census DOES NOT MOVE and the new cost is
 * confined to the sun's shadow term. Both copies of `validity` are written by ONE expression from
 * ONE batch column, so they cannot disagree.
 *
 * `.yz` are ZERO and that zero is their DATA, not a spare slot. `probeGrid`'s deleted `.g` channel
 * is this repo's own precedent that a written-but-unread channel is how a format grows a field
 * that only looks maintained: a future occupant (sky/ambient visibility — the skylight is
 * unoccluded beyond the cascades for exactly the reason the sun was; or a bent normal) is a FORMAT
 * BUMP, not a free channel.
 */
export const SUN_VIS_TEXEL = SH_TEXELS; // 7

/**
 * ⭐ THE CAGE WEIGHT AT WHICH BAND 7's MEASUREMENT IS BELIEVED IN FULL — `Σ wᵢ·vᵢ` (band 7's own
 * `.w`), below which the answer is faded toward *the sun is unshadowed* rather than divided out.
 *
 * ── WHY A SHADOW TERM NEEDS THIS AND THE SH DOES NOT ────────────────────────────────────────────
 *
 * The two quantities in this format fail in OPPOSITE directions. `shIrradiance` divides by the
 * same denominator and a small one yields a NOISY colour — wrong, but bounded, non-negative and
 * plausible. Band 7's quotient with a small denominator yields a number near ZERO, and zero here
 * is **fully shadowed**: the loudest possible wrong answer, painted over a whole region, in the
 * one term of the frame nobody can check by eye. So the rule this constant enforces is the one
 * `probeScatter.writeSunVisibleFillBand` already states for the fill brick, applied to the arm
 * that bypasses it:
 *
 *   ⭐ **FOR A SHADOW TERM, "I DO NOT KNOW" MUST MEAN "DO NOT SHADOW", NEVER "FULLY SHADOW".**
 *
 * ⚠️ IT IS A CONFIDENCE FACTOR, NOT A FLOOR ON THE DENOMINATOR. `sv.w.max(0.25)` would scale a
 * marginal cage's answer TOWARD ZERO, which is the one direction a shadow term may not fail in;
 * `saturate(sv.w / 0.25)` blends it toward 1. Same two ALU ops, opposite failure.
 *
 * ⚠️ **THE THRESHOLD IS DERIVED FROM THE DIVIDE'S OWN FLOOR, AND A LARGER ONE IS A BUG.** The
 * quotient is `x / max(w, 1e-3)`. Above that floor the divide is DIVIDING BY THE REAL CAGE WEIGHT,
 * and the premultiplied format's whole point is that the weight then cancels: a probe 94 % buried
 * still recovers its visibility EXACTLY (`tests/renderSparseAddress.test.ts` pins that round-trip
 * at validities 1, 0.5 and 0.0625). Below the floor the denominator is a CONSTANT the shader
 * substituted, so the quotient is manufactured rather than measured, and it is manufactured
 * *downward* — toward black.
 *
 * So this is `10 × 1e-3`: the smallest weight at which the divide still has an order of magnitude
 * of headroom over its own floor. The ramp therefore lives ENTIRELY inside the region where the
 * arithmetic has stopped measuring. ⚠️ A first draft used 0.25 — one probe of the eight-probe cage
 * — and it was WRONG in a way a test caught: at validity 0.0625 it returned 0.8125 for a stored
 * 0.25 and broke the format's validity-independence for every legitimately buried probe. A cage at
 * 6 % validity is not saying *"I do not know"*; it is saying *"the part of me that could see said
 * 0.25"*, and that is a measurement this term may not discard.
 *
 * ⚠️ AND ITS ARM IS PROVABLY INERT ON THE OWNER'S REAL BAKE, WHICH IS WHY IT IS SAFE AND WHY IT
 * IS ALSO NOT THE FIX. Measured over **3,633,120 samples** (a 0.5 m lattice across his whole
 * sponza-debug-01 grid, four normals each, through the real `sparseAddress` chain): the minimum
 * `Σ wᵢ·vᵢ` is **0.55138**, 55× this threshold, and zero samples fall below even 0.25 — so on his
 * frame this factor is exactly 1.0 everywhere and the emitted value is bit-identical. Dilation
 * restores validity and dilates band 7 by construction, so a fully-invalid cage is not reachable
 * with dilation on. It is here for the bake that has dilation off, and it has a fixture test that
 * makes it say YES (`tests/renderSparseAddress.test.ts`, the `withSunVis(0, …)` case).
 */
export const SUN_CAGE_FULL_WEIGHT = 1e-2;

/**
 * ⚠️ **THE PER-BAND FALLBACK RULE — WHAT A BAND OWES WHEN THE ADDRESS LEAVES THE VOLUME.**
 *
 * The clamp in `sparseAddress` is an ADDRESSING decision and it must stay shared: one address
 * chain, one answer to *"where am I in this volume"*. What each band owes when that address is
 * outside the data is a decision PER BAND, and the two bands this format has answer differently:
 *
 *   - **The SH (bands 0…6)** keep the clamped boundary value. The field is smooth and
 *     non-negative, the nearest known probe is the least-wrong extrapolation, and the measured
 *     error is a FACTOR (0.8716 outside sponza against 0.6604 inside its arcade), not a sign flip.
 *   - **Band 7** does not. `sparseFetch` fades it to 1.0 outside the grid, because the clamped
 *     boundary brick's visibility is uncorrelated with the sun's visibility out there and can be
 *     exactly 0 — measured on the owner's bake, held to 200 m. See `SUN_OUT_OF_VOLUME_FADE_TEXELS`.
 *
 * A band added to this format must declare which of those it is, HERE, before it is packed.
 */

/**
 * ⭐ ATLAS BANDS PER PROBE — the SH half plus the sun-visibility band. EIGHT RGBA texels.
 *
 * This is THE number the scatter, the dilation, the capture atlas, the file's payload arithmetic
 * and the shader's z stride all read, and it is still the ONLY band count anything outside this
 * file reads.
 *
 * ⚠️ THESE THREE NAMES ARE NOT `BRICK_BANDS`'s SIBLINGS, AND THE DIFFERENCE IS THE WHOLE POINT.
 * F2 added `BRICK_BANDS = SUB_VOLUMES + OCT_TEXELS` (71) and every consumer then had to know
 * which of TWO NAMES FOR ONE ROLE it meant — `probeDilate`'s channel count meant one,
 * `packShTexel`'s packing meant the other, and the file header recorded the first under the NAME
 * of the second (`structure.subVolumes`). O3 deleted it rather than aliasing it. `SH_TEXELS`,
 * `SUN_VIS_TEXEL` and `SUB_VOLUMES` are THREE DIFFERENT VALUES with three different meanings —
 * *how many texels the SH occupies* · *which index the visibility band has* · *how many bands the
 * format has* — each derived from the one before, in one file. A consumer that wants "how many
 * bands" has exactly one answer, and a consumer that wants "how much of a probe is SH" now has one
 * too instead of open-coding a 7.
 *
 * ⚠️ AND THE LANDMINE THIS MOVE ARMS IS NAMED AT ITS SITE: `probeDilate.VALIDITY_CHANNEL` was
 * written `SUB_VOLUMES * 4 - 1` and would have followed this constant from channel 27 to channel
 * 31 — band 7's unused `.z`-adjacent `.w`, which carries validity but is not the SH's denominator.
 * It reads `SH_TEXELS * 4 - 1` there now, as a literal-checked derivation.
 */
export const SUB_VOLUMES = SH_TEXELS + 1; // 8

/** A brick's atlas footprint in texels: every band is stacked INSIDE the brick. */
export const BRICK_FOOTPRINT = [BRICK_EDGE, BRICK_EDGE, SUB_VOLUMES * BRICK_EDGE] as const; // 5,5,40

/** Texels one brick occupies in the atlas. */
export const BRICK_TEXELS = BRICK_FOOTPRINT[0] * BRICK_FOOTPRINT[1] * BRICK_FOOTPRINT[2]; // 1000

/** Bytes per unique probe. `SUB_VOLUMES × RGBA × 2 B` of binary16. */
export const BYTES_PER_PROBE = SUB_VOLUMES * 4 * 2; // 64

/** Bytes per atlas texel — RGBA16F. */
export const BYTES_PER_TEXEL = 8;

/**
 * Atlas brick-grid ceilings: `maxTextureDimension3D` (2048, the WebGPU default limit) ÷ footprint.
 *
 * ⚠️ DERIVED, not written down. It used to be the literal `[409, 409, 58]`, which was correct for
 * a 35-deep footprint and was silently WRONG for the 355-deep one the depth bands made (the true
 * ceiling there was 5) — a ceiling that no longer describes the texture it is a ceiling for admits
 * an allocation the device creates INVALID (`AtlasDimensionError`'s own note: it reads back zero
 * and renders a dark room with every self-report healthy). Deriving it is also what made the cut
 * back to 35 cost nothing here: the numbers moved with the footprint, inside one expression.
 */
const MAX_3D = 2048;
export const BRICK_GRID_MAX = [
  Math.floor(MAX_3D / BRICK_FOOTPRINT[0]),
  Math.floor(MAX_3D / BRICK_FOOTPRINT[1]),
  Math.floor(MAX_3D / BRICK_FOOTPRINT[2]),
] as const;

/**
 * The brick grid's z divisor, so `probeCompile` can keep the atlas roughly cubic IN TEXELS without
 * re-deriving the footprint ratio. `(footprintZ / footprintX)²` — 64 at the `[5,5,40]` footprint
 * `axon.probeVolume/5` has, 49 while it was `[5,5,35]`, and 529 for as long as the depth bands
 * made it `[5,5,355]`.
 *
 * ⚠️ IT MOVES WITH THE BAND COUNT, so the compiled `brickGrid` for a given brick count can differ
 * from every number pinned before `/5` with no bug anywhere — `tests/renderSparsePlacement.test.ts`
 * failing is that pin doing its job, not a regression. Sponza's 728 bricks stay at `[16,16,3]`
 * (⌈∛(729/64)⌉ = 3 either way), so on the one world with an atlas the bytes are the whole delta
 * and no slot reassignment rides along.
 */
export const BRICK_GRID_Z_BIAS = (BRICK_FOOTPRINT[2] / BRICK_FOOTPRINT[0]) ** 2;

/** Density levels an artist may author. `spacing = baseSpacingM × 2^level`. */
export const MAX_LEVEL = 4;

/**
 * Indirection grid ceiling per axis. 4096 is NOT a hardware limit — it is the number that makes
 * the shader's float clamp margin provable (spec §4.3): fp32 ULP at 4096 is 4.9e-4 against a
 * margin of 0.125, a 255x headroom. Raising this invalidates that proof, so it is a refusal here
 * rather than a clamp.
 */
export const INDIR_DIM_MAX = 4096;

/**
 * Atlas brick slot 0 is RESERVED and all-zero, and is normally UNREACHABLE: with
 * `fillEmptySpaces: true` (the default) every cell inside the grid resolves to a real brick. It
 * exists for `fillEmptySpaces: false`, which is retained for measurement and for S1's corruption
 * assertion, and which produces a hard black boundary BY DESIGN.
 */
export const EMPTY_BRICK = 0;

/** Probes captured per bake chunk = the batch target's height. INDEPENDENT of probe count. */
export const BAKE_CHUNK = 128;

/** Captures per capture-atlas layer group. The capture atlas is 2D-TILED — spec §3.2. */
export const CAPTURES_PER_LAYER = BAKE_CHUNK * BAKE_CHUNK; // 16384
